/**
 * Saved-itinerary list display state, section buckets, and persistence handlers.
 *
 * @remarks Colocated with `itineraryList/`. Intentionally separate from
 * {@link useItineraryListDataFlow} so AbortController race logic stays isolated.
 */
import { useCallback, useMemo, useState } from "react";
import { SEARCH_BAR } from "../../../config/global.configuration";
import { usePageVisibility } from "../../../hooks/usePageVisibility.hook";
import { useReducedMotion } from "../../../hooks/useReducedMotion.hook";
import { savedItinerariesSlice, usePersistedSlice } from "../../../store/persistence";
import { type SavedItineraryEntry } from "../../../store/persistence/slices/savedItineraries.slice";
import {
    isItinerarySaved,
    removeSavedItinerary,
    toggleSavedItinerary,
} from "../../../tools/savedItinerary.tools";
import {
    prepareSavedItinerariesForDisplay,
    prepareSavedItineraryEntriesForDisplay,
    resolveGeocodeItemDisplayLabel,
    resolveSavedItineraryDestinationLabel,
    resolveSavedItineraryOriginLabel,
    resolveSavedItineraryTemporalSection,
} from "../../../tools/savedItineraryDisplay.tools";
import { type ItineraryDTO } from "../../../types/dtos/api/itinerary/itinerary.dto";
import { type ContainerProps } from "../../../types/ItineraryPlanner";
import { isUiMotionEnabled } from "../../shared/motion/uiMotion.tools";

/** Parameters for {@link useSavedItineraryListState}. */
export interface UseSavedItineraryListStateParams {
    "itineraryListContentMode":    ContainerProps["itineraryListContentMode"];
    "setItineraryListContentMode": ContainerProps["setItineraryListContentMode"];
    "activeSavedFilterTab":        ContainerProps["activeSavedFilterTab"];
    "selectedItinerary":           ContainerProps["selectedItinerary"];
    "setSelectedItinerary":        ContainerProps["setSelectedItinerary"];
    "itineraries":                 ContainerProps["itineraries"];
    "departureSelectedAddress":    ContainerProps["departureSelectedAddress"];
    "selectedAddress":             ContainerProps["selectedAddress"];
    "selectedInputs":              ContainerProps["selectedInputs"];
    "setVisible":                  ContainerProps["setVisible"];
    "onCloseSearchResults":        ContainerProps["onCloseSearchResults"];

    /** Shows a danger toast when save toggle fails. */
    "presentErrorToast": ( message: string ) => void;
}

/** Result of {@link useSavedItineraryListState}. */
export interface UseSavedItineraryListStateResult {
    "isSavedListMode":                    boolean;
    "showItineraryDetailsPrice":          boolean;
    "isSelectedItinerarySaved":           boolean;
    "displayedItineraries":               ItineraryDTO[];
    "pastDisplayedSavedEntries":          SavedItineraryEntry[];
    "currentDisplayedSavedEntries":       SavedItineraryEntry[];
    "upcomingDisplayedSavedEntries":      SavedItineraryEntry[];
    "savedEmptyStateVariant":             "noSaved" | "noUpcoming" | "filteredEmpty";
    "shouldRenderSavedListShell":         boolean;
    "itineraryDetailsDepartureAddress":   ContainerProps["departureSelectedAddress"] | { "result": string };
    "itineraryDetailsDestinationAddress": ContainerProps["selectedAddress"] | { "result": string };
    "handleSavedItineraryToggle":         () => void;
    "handleRemoveSavedItinerary":         ( itineraryId: string ) => void;
    "handleCloseList":                    () => void;
    "handleSavedListExitComplete":        () => void;
}

/**
 * Derives saved-list display buckets and persistence handlers for the itinerary panel.
 *
 * @param params Content mode, itineraries, address labels, and toast bridge.
 * @returns Saved-list view model and handlers for panel views.
 */
export const useSavedItineraryListState = ( params: UseSavedItineraryListStateParams ): UseSavedItineraryListStateResult => {
    const {
        itineraryListContentMode,
        setItineraryListContentMode,
        activeSavedFilterTab,
        selectedItinerary,
        setSelectedItinerary,
        itineraries,
        departureSelectedAddress,
        selectedAddress,
        selectedInputs,
        setVisible,
        onCloseSearchResults,
        presentErrorToast,
    } = params;

    const reducedMotion = useReducedMotion();
    const [ savedItineraries, setSavedItineraries ] = usePersistedSlice( savedItinerariesSlice );
    const [ keepSavedListPresence, setKeepSavedListPresence ] = useState( false );

    const isSavedListMode = itineraryListContentMode === "saved";

    /**
     * Recompute past / current / upcoming buckets when the user returns to the
     * app/tab, when opening the saved list, and once per minute while it stays open.
     */
    const { nowMs } = usePageVisibility( {
        "enabled":        isSavedListMode,
        "trackNow":       true,
        "tickIntervalMs": SEARCH_BAR.refreshTimeIntervalMs,
    } );
    const showItineraryDetailsPrice = !isSavedListMode;
    const isSelectedItinerarySaved = useMemo( () => {
        return isItinerarySaved( savedItineraries, selectedItinerary?.id );
    }, [ savedItineraries, selectedItinerary?.id ] );

    const displayedSavedEntries = useMemo( () => {
        if ( !isSavedListMode ) {
            return [];
        }

        return prepareSavedItineraryEntriesForDisplay( savedItineraries, {
            "showPast":    true,
            "filterTabId": activeSavedFilterTab,
            nowMs,
        } );
    }, [
        activeSavedFilterTab,
        isSavedListMode,
        nowMs,
        savedItineraries,
    ] );
    const pastDisplayedSavedEntries = useMemo( () => {
        return displayedSavedEntries.filter( ( entry ) => resolveSavedItineraryTemporalSection( entry, nowMs ) === "past" );
    }, [
        displayedSavedEntries,
        nowMs,
    ] );
    const currentDisplayedSavedEntries = useMemo( () => {
        return displayedSavedEntries.filter( ( entry ) => resolveSavedItineraryTemporalSection( entry, nowMs ) === "current" );
    }, [
        displayedSavedEntries,
        nowMs,
    ] );
    const upcomingDisplayedSavedEntries = useMemo( () => {
        return displayedSavedEntries.filter( ( entry ) => resolveSavedItineraryTemporalSection( entry, nowMs ) === "upcoming" );
    }, [
        displayedSavedEntries,
        nowMs,
    ] );
    const displayedItineraries = useMemo( () => {
        if ( !isSavedListMode ) {
            return itineraries;
        }

        return prepareSavedItinerariesForDisplay( savedItineraries, {
            "showPast":    true,
            "filterTabId": activeSavedFilterTab,
            nowMs,
        } );
    }, [
        activeSavedFilterTab,
        isSavedListMode,
        itineraries,
        nowMs,
        savedItineraries,
    ] );
    const selectedSavedEntry = useMemo( () => {
        if ( selectedItinerary?.id == null ) {
            return undefined;
        }

        return savedItineraries.find( ( entry ) => entry.itinerary.id === selectedItinerary.id );
    }, [ savedItineraries, selectedItinerary ] );
    const itineraryDetailsDepartureAddress = useMemo( () => {
        if ( selectedSavedEntry !== undefined ) {
            return { "result": resolveSavedItineraryOriginLabel( selectedSavedEntry ) };
        }

        return departureSelectedAddress;
    }, [ departureSelectedAddress, selectedSavedEntry ] );
    const itineraryDetailsDestinationAddress = useMemo( () => {
        if ( selectedSavedEntry !== undefined ) {
            return { "result": resolveSavedItineraryDestinationLabel( selectedSavedEntry ) };
        }

        return selectedAddress;
    }, [ selectedAddress, selectedSavedEntry ] );

    /**
     * Persists or removes the currently selected saved itinerary.
     */
    const handleSavedItineraryToggle = useCallback( (): void => {
        if ( selectedItinerary === null ) {
            return;
        }

        const originLabel = resolveGeocodeItemDisplayLabel( departureSelectedAddress ) ??
          resolveGeocodeItemDisplayLabel( selectedInputs.departureAddress );
        const destinationLabel = resolveGeocodeItemDisplayLabel( selectedAddress ) ??
          resolveGeocodeItemDisplayLabel( selectedInputs.arrivalAddress );

        try {
            const { nextSavedItineraries } = toggleSavedItinerary( savedItineraries, selectedItinerary, {
                ...originLabel !== undefined ? { originLabel } : {},
                ...destinationLabel !== undefined ? { destinationLabel } : {},
            } );
            setSavedItineraries( nextSavedItineraries );
        } catch( error: unknown ) {
            console.error( "[ItineraryList] Failed to toggle saved itinerary", error );
            presentErrorToast( "Impossible d'enregistrer cet itinéraire" );
        }
    }, [
        departureSelectedAddress,
        presentErrorToast,
        savedItineraries,
        selectedAddress,
        selectedInputs.arrivalAddress,
        selectedInputs.departureAddress,
        selectedItinerary,
        setSavedItineraries,
    ] );

    /**
     * Removes one saved itinerary from the persisted list.
     *
     * @param itineraryId Stable itinerary id to remove.
     */
    const handleRemoveSavedItinerary = useCallback( ( itineraryId: string ): void => {
        if (
            displayedSavedEntries.length === 1 &&
            isUiMotionEnabled( reducedMotion )
        ) {
            setKeepSavedListPresence( true );
        }

        setSavedItineraries( ( previousSavedItineraries ) => {
            return removeSavedItinerary( previousSavedItineraries, itineraryId );
        } );
        setSelectedItinerary( ( previousSelectedItinerary ) => {
            if ( previousSelectedItinerary?.id === itineraryId ) {
                return null;
            }

            return previousSelectedItinerary;
        } );
    }, [
        displayedSavedEntries.length,
        reducedMotion,
        setSavedItineraries,
        setSelectedItinerary,
    ] );

    /**
     * Closes saved-itinerary mode and hides the itinerary panel.
     */
    const handleCloseSavedList = useCallback( (): void => {
        setKeepSavedListPresence( false );
        setItineraryListContentMode( "search" );
        setSelectedItinerary( null );
        setVisible( false );
    }, [ setSelectedItinerary, setItineraryListContentMode, setVisible ] );

    const handleCloseList = isSavedListMode ? handleCloseSavedList : onCloseSearchResults;

    const savedEmptyStateVariant = useMemo( (): "noSaved" | "noUpcoming" | "filteredEmpty" => {
        if ( savedItineraries.length === 0 ) {
            return "noSaved";
        }

        if (
            pastDisplayedSavedEntries.length > 0 &&
            currentDisplayedSavedEntries.length === 0 &&
            upcomingDisplayedSavedEntries.length === 0
        ) {
            return "noUpcoming";
        }

        return "filteredEmpty";
    }, [
        currentDisplayedSavedEntries.length,
        pastDisplayedSavedEntries.length,
        savedItineraries.length,
        upcomingDisplayedSavedEntries.length,
    ] );

    const handleSavedListExitComplete = useCallback( (): void => {
        if ( displayedSavedEntries.length === 0 ) {
            setKeepSavedListPresence( false );
        }
    }, [ displayedSavedEntries.length ] );

    const shouldRenderSavedListShell = displayedSavedEntries.length > 0 || keepSavedListPresence;

    return {
        isSavedListMode,
        showItineraryDetailsPrice,
        isSelectedItinerarySaved,
        displayedItineraries,
        pastDisplayedSavedEntries,
        currentDisplayedSavedEntries,
        upcomingDisplayedSavedEntries,
        savedEmptyStateVariant,
        shouldRenderSavedListShell,
        itineraryDetailsDepartureAddress,
        itineraryDetailsDestinationAddress,
        handleSavedItineraryToggle,
        handleRemoveSavedItinerary,
        handleCloseList,
        handleSavedListExitComplete,
    };
};
